import React, { useState, useEffect } from 'react';
import { SEOHead } from '../../components/public/SEOHead';
import {
  Sparkles,
  ChevronRight,
  LayoutDashboard,
  ArrowRight,
  Zap,
  Play,
  ShieldCheck,
  CheckCircle2,
  Lock
} from 'lucide-react';

interface HomePageProps {
  onNavigate: (path: string) => void;
  onOpenAuth: (mode: 'login' | 'register') => void;
}

const InteractiveDemoStudio = React.lazy(() =>
  import('../../components/public/InteractiveDemoStudio').then(m => ({ default: m.InteractiveDemoStudio }))
);

const HomePageBelowTheFold = React.lazy(() =>
  import('../../components/public/HomePageBelowTheFold').then(m => ({ default: m.HomePageBelowTheFold }))
);

export const HomePage: React.FC<HomePageProps> = ({ onNavigate, onOpenAuth }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    try {
      setIsLoggedIn(!!localStorage.getItem('scalebulk_token'));
    } catch {
      setIsLoggedIn(false);
    }
  }, []);

  const handleScrollToDemo = () => {
    const el = document.getElementById('interactive-demo-studio');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      onNavigate('/how-it-works');
    }
  };

  const HOMEPAGE_SCHEMA = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://www.scalebulk.com/#organization',
        name: 'Scale Bulk',
        url: 'https://www.scalebulk.com/',
        logo: 'https://www.scalebulk.com/brand/scalebulk-logo-stacked-dark.png',
        sameAs: [
          'https://x.com/scalebulk',
          'https://www.linkedin.com/company/scale-bulk'
        ],
        founder: {
          '@type': 'Person',
          name: 'Er. Deepak Kumar',
          jobTitle: 'Founder',
          sameAs: 'https://in.linkedin.com/in/dipak-scalebulk'
        },
        description: 'Scale Bulk is an AI-powered social media management platform that helps businesses, creators, marketing teams and agencies create, optimize, schedule, publish and analyze social media content from one workspace.'
      },
      {
        '@type': 'WebSite',
        '@id': 'https://www.scalebulk.com/#website',
        url: 'https://www.scalebulk.com/',
        name: 'Scale Bulk',
        publisher: { '@id': 'https://www.scalebulk.com/#organization' },
        description: 'AI Social Media Management & One-Click Multi-Platform Publishing',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://www.scalebulk.com/blog?q={search_term_string}',
          'query-input': 'required name=search_term_string'
        }
      },
      {
        '@type': 'SoftwareApplication',
        '@id': 'https://www.scalebulk.com/#software',
        name: 'Scale Bulk',
        operatingSystem: 'Web, Cloud, SaaS, All Modern Browsers',
        applicationCategory: 'BusinessApplication, SocialMediaManagementApplication',
        description: 'AI-powered social media management, automated scheduling, one-click publishing, and analytics platform across 7 networks.',
        url: 'https://www.scalebulk.com/',
        offers: {
          '@type': 'Offer',
          price: '0.00',
          priceCurrency: 'USD',
          description: 'Free starter tier available with multi-channel publishing'
        },
        featureList: [
          'One-Click Multi-Platform Publishing',
          'Scale Bulk Automated Copy Adaptation',
          'Automated Scheduling & Timezone Queuing',
          'Multi-Channel Unified Analytics & Telemetry',
          'Isolated Client Workspaces for Digital Marketing Agencies',
          'Direct Official APIs: Instagram, Facebook, LinkedIn, TikTok, X, YouTube, Pinterest'
        ]
      },
      {
        '@type': 'FAQPage',
        '@id': 'https://www.scalebulk.com/#faq',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What is Scale Bulk?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Scale Bulk is an AI-powered social media management platform that helps businesses, creators, marketing teams and agencies create, optimize, schedule, publish and analyze social media content from one workspace.'
            }
          },
          {
            '@type': 'Question',
            name: 'How does Scale Bulk work?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'You connect your social accounts once via official OAuth 2.0. When drafting a campaign, you write your core idea once. Scale Bulk adapts the copy, length, and hashtags for each platform format. You can then publish immediately or queue it to our automated scheduling worker.'
            }
          },
          {
            '@type': 'Question',
            name: 'How does one-click publishing work?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'When you click "Publish Everywhere", our backend securely retrieves your encrypted OAuth access tokens from the AES-256-GCM vault and sends simultaneous API requests to all connected platforms with the platform-specific formatted content.'
            }
          },
          {
            '@type': 'Question',
            name: 'Which platforms can Scale Bulk connect to?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Scale Bulk supports official integrations with Instagram, Facebook, LinkedIn, TikTok, X (Twitter), YouTube, and Pinterest through their official developer APIs.'
            }
          },
          {
            '@type': 'Question',
            name: 'How does Scale Bulk create captions?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Scale Bulk analyzes your prompt, target platform algorithms, audience context, and optional Brand Memory guidelines. It generates natural, high-converting copy adapted for specific character limits and platform conventions.'
            }
          },
          {
            '@type': 'Question',
            name: 'Can I schedule posts?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. Scale Bulk provides an intuitive calendar and smart scheduler. You can schedule individual posts, queue recurring slots, or use AI-recommended peak engagement hours calculated per platform.'
            }
          },
          {
            '@type': 'Question',
            name: 'Can agencies manage multiple clients?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Absolutely. Scale Bulk includes isolated agency workspaces with dedicated social connections, custom Brand Memory guidelines, role-based team permissions, and client approval workflows.'
            }
          },
          {
            '@type': 'Question',
            name: 'Is my social media account credentials secure?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'All account credentials and OAuth 2.0 access tokens are encrypted with military-grade AES-256-GCM encryption in isolated storage vaults. Scale Bulk never stores your personal social account passwords.'
            }
          },
          {
            '@type': 'Question',
            name: 'Which social platforms are supported?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Scale Bulk supports direct integration with Facebook Pages, Instagram Professional Accounts, LinkedIn Profiles & Company Pages, TikTok Accounts, X (Twitter), YouTube Channels, and Pinterest Business Accounts.'
            }
          },
          {
            '@type': 'Question',
            name: 'Where can I store my media?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Scale Bulk supports direct media uploads with automated optimization, as well as native Google Drive integration allowing you to import your creative assets directly from your cloud storage.'
            }
          },
          {
            '@type': 'Question',
            name: 'How does Google Drive storage work?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'You can authenticate your Google account via secure Google Picker. Scale Bulk streams selected video and image assets for post publishing without requiring permanent duplication on proprietary file servers.'
            }
          },
          {
            '@type': 'Question',
            name: 'What happens when a social connection fails?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'If a social network expires an authorization token or rejects a post, Scale Bulk logs the exact error code from the official platform API, sends an instant notification, and preserves your draft in the retry queue.'
            }
          }
        ]
      }
    ]
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', overflowX: 'hidden' }}>
      <SEOHead
        title="Scale Bulk | AI Social Media Management & One-Click Publishing"
        description="Create, optimize, schedule and publish social media content across connected platforms with Scale Bulk's AI-powered social media management platform."
        canonicalPath="/"
        keywords="social media management, one-click publishing, AI social media scheduler, multi-channel distribution, social media automation, agency workspace, Instagram, LinkedIn, TikTok, X, YouTube, Facebook, Pinterest"
        breadcrumbs={[{ name: 'Home', url: '/' }]}
        schema={HOMEPAGE_SCHEMA}
      />

      {/* SECTION 1: HERO */}
      <section style={{ position: 'relative', padding: '44px 20px 36px', overflow: 'hidden', textAlign: 'center' }}>
        <div className="hero-aurora-orb" style={{ position: 'absolute', top: '-100px', left: '15%', width: '650px', height: '420px', borderRadius: '50%', background: 'radial-gradient(ellipse at center, rgba(99, 102, 241, 0.28) 0%, rgba(168, 85, 247, 0.16) 45%, transparent 70%)', filter: 'blur(75px)', animation: 'auroraFloat 14s ease-in-out infinite', pointerEvents: 'none', zIndex: 0 }} />
        <div className="hero-aurora-orb" style={{ position: 'absolute', top: '40px', right: '10%', width: '580px', height: '400px', borderRadius: '50%', background: 'radial-gradient(ellipse at center, rgba(236, 72, 153, 0.25) 0%, rgba(244, 63, 94, 0.14) 45%, transparent 70%)', filter: 'blur(80px)', animation: 'auroraFloat 18s ease-in-out infinite reverse', pointerEvents: 'none', zIndex: 0 }} />
        <div className="hero-aurora-orb" style={{ position: 'absolute', top: '260px', left: '35%', width: '500px', height: '320px', borderRadius: '50%', background: 'radial-gradient(ellipse at center, rgba(6, 182, 212, 0.2) 0%, rgba(16, 185, 129, 0.12) 45%, transparent 70%)', filter: 'blur(70px)', animation: 'auroraFloat 16s ease-in-out infinite 3s', pointerEvents: 'none', zIndex: 0 }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px)', backgroundSize: '32px 32px', pointerEvents: 'none', zIndex: 0 }} />

        <div style={{ maxWidth: '1220px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div className="liquid-glass-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '5px 16px', borderRadius: '999px', marginBottom: '14px', border: '1px solid rgba(99, 102, 241, 0.35)', boxShadow: '0 0 20px rgba(99, 102, 241, 0.2)' }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#34d399', boxShadow: '0 0 8px #34d399' }} />
            <Sparkles size={13} color="#f472b6" />
            <span style={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.03em' }}>Scale Bulk 3.0 • One-Click Multi-Channel AI Distribution</span>
            <ChevronRight size={13} color="#cbd5e1" />
          </div>

          <h1 className="hero-title-luminous" style={{ fontSize: 'clamp(2.3rem, 4.6vw, 3.8rem)', fontWeight: 900, lineHeight: 1.18, letterSpacing: '-0.04em', maxWidth: '920px', margin: '0 auto 14px', wordBreak: 'break-word' }}>
            Create Once.<br />
            <span className="hero-title-gradient">Publish Everywhere.</span>
          </h1>

          <p className="hero-subtitle-high-contrast" style={{ fontSize: '1.08rem', lineHeight: 1.55, maxWidth: '700px', margin: '0 auto 20px', fontWeight: 450 }}>
            The unified social distribution studio. Draft high-converting campaigns once—Scale Bulk automatically adapts tone, character constraints, hashtags, and formats across connected <strong style={{ fontWeight: 700 }}>Facebook Pages, Instagram, LinkedIn, TikTok, X, YouTube, and Pinterest</strong> with verified Graph APIs.
          </p>

          <div className="hero-cta-buttons-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '20px' }}>
            {isLoggedIn ? (
              <button
                onClick={() => onNavigate('/dashboard')}
                className="btn-magic-luminous"
                style={{ padding: '14px 32px', fontSize: '0.98rem', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: '9px', borderRadius: '12px' }}
              >
                <LayoutDashboard size={18} />
                <span>Open Studio Workspace</span>
                <ArrowRight size={17} />
              </button>
            ) : (
              <button
                onClick={() => onOpenAuth('register')}
                className="btn-magic-luminous"
                style={{ padding: '14px 32px', fontSize: '0.98rem', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: '9px', borderRadius: '12px' }}
              >
                <Zap size={18} />
                <span>Start Free — No Credit Card Required</span>
                <ArrowRight size={17} />
              </button>
            )}
            <button
              onClick={handleScrollToDemo}
              className="liquid-glass-chip"
              style={{ padding: '13px 24px', fontSize: '0.92rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '8px', borderRadius: '12px', cursor: 'pointer' }}
            >
              <Play size={14} color="#f472b6" fill="#f472b6" />
              <span>Watch Live Interactive Studio Demo</span>
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', flexWrap: 'wrap', fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '26px' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <ShieldCheck size={15} color="#60a5fa" /> Meta Graph API v21.0 Verified
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <CheckCircle2 size={15} color="#38bdf8" /> LinkedIn Community API
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <CheckCircle2 size={15} color="#22d3ee" /> TikTok Content Posting
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <CheckCircle2 size={15} color="#f87171" /> YouTube Data API v3
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <Lock size={14} color="#34d399" /> AES-256 Vault Encryption
            </span>
          </div>

          {/* Interactive Demo Console */}
          <div style={{ position: 'relative', width: '100%' }}>
            <div className="motion-float-badge hero-floating-badge float-slow-1 hide-on-mobile" style={{ top: '-16px', left: '-8px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#34d399', boxShadow: '0 0 10px #34d399' }} />
              <span>⚡ Official Platform API Connections</span>
            </div>
            <div className="motion-float-badge hero-floating-badge float-slow-2 hide-on-mobile" style={{ top: '-16px', right: '-8px' }}>
              <Sparkles size={13} color="#f472b6" />
              <span>Multi-Channel Draft &amp; Schedule</span>
            </div>
            <div className="motion-float-badge hero-floating-badge float-slow-3 hide-on-mobile" style={{ bottom: '-14px', right: '32px' }}>
              <Lock size={13} color="#38bdf8" />
              <span>OAuth 2.0 Secure Authorization</span>
            </div>

            <React.Suspense fallback={
              <div id="interactive-demo-studio" className="hero-compact-container glass-glow-card" style={{ minHeight: '340px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading Live Interactive Studio...</span>
              </div>
            }>
              <InteractiveDemoStudio onNavigate={onNavigate} onOpenAuth={onOpenAuth} user={isLoggedIn ? { id: 'current' } : null} />
            </React.Suspense>

          </div>
        </div>
      </section>

      {/* DEFERRED BELOW-THE-FOLD CONTENT (SECTIONS 2-13) */}
      <React.Suspense fallback={<div style={{ minHeight: '600px', background: 'var(--bg-secondary)' }} />}>
        <HomePageBelowTheFold onNavigate={onNavigate} onOpenAuth={onOpenAuth} />
      </React.Suspense>
    </div>
  );
};
